/**
 * The five rows under the ticket inputs — components.md §2.4.
 *
 * ```
 * Cost          $63.00       ← 100 shares × 0.63, exactly
 * Fee            $0.00       ← always rendered, even though f = 0.0000 at MVP
 * To win        $100.00      ← up-text when the position is UP, down-text when DOWN
 * Return        +58.7%       ← always signed
 * Avg price     0.6300
 * ```
 *
 * ## The `Fee` row is shown at zero and that is the point
 *
 * D12 ships the fee *mechanism* with the rate set to `0.0000`, so the honest
 * thing to show is a visible zero: it tells the user there is no fee rather than
 * leaving them to assume one is hidden in the cost, and the row does not
 * materialise out of nowhere the day the rate becomes non-zero.
 *
 * components.md §2.4 says the value "comes from the server's `fee` field on the
 * fill preview, never from a client-side constant, **including not from a
 * hardcoded zero**" — and there is no fill-preview endpoint. §7.9 settles it
 * without one: the ticket shows `[fee(best_reachable), fee(worst_reachable)]`
 * computed from `config.fees` and the live book, "and at the launch rates that
 * range collapses to `[0.000000, 0.000000]` and the ticket renders a plain
 * `Fee $0.00`". So the zero on screen is the output of
 * `feeMicroUsdc(size, config.fees.taker_fee_rate, price)` — the same expression
 * that will render a real fee the day the rate moves — and never a literal.
 * Register §F22f.
 *
 * Recomputed on every orderbook tick while the ticket is open, which is why the
 * quote arrives as a prop: this component subscribes to nothing.
 *
 * ## Selling adds a row rather than a second component — B7.5
 *
 * A sell is quoted against the **complement's** ladder, because that is where
 * its counterparty is: `SELL UP @ 51` and `BUY DOWN @ 49` are one interest on
 * one book (§7.8). So `quote` arriving here for a sell is already a quote of the
 * complement, and `outcome` is already the complement — nothing in this file
 * transforms anything.
 *
 * What a sell changes is which of these five numbers are about the user. The
 * covered part **returns** money and mints nothing; the uncovered part **costs**
 * money and mints the complement. So `sell` carries the covered part's proceeds
 * and its price in the user's own terms (§7.8: "every price a user sees is in
 * the terms they traded in"), a `Proceeds` row appears above `Cost`, and `Cost`,
 * `To win` and `Return` are blanked when nothing is being minted — because a
 * close has no payout to preview and rendering the complement's would be a
 * number about an order the user is not placing.
 */

import { memo } from 'react'
import { cx } from '@/lib/cx'
import { formatFixed, USDC_DP, type Outcome } from '@/lib/decimal'
import { formatUsdc } from '@/lib/format'
import type { Quote } from '@/domain/quote'

/** The half of a sell that returns money rather than spending it. */
export interface SellPreview {
  /** Micro-shares being closed rather than minted. `0n` on a pure mint. */
  coveredShares: bigint
  /** Micro-shares being minted rather than closed. `0n` on a pure close. */
  openingShares: bigint
  /** Micro-USDC the covered part returns, at the touch. */
  proceedsMicroUsdc: bigint
  /** The user's own sell price, cents at 4dp — `100 − ` the complement's. */
  sellPriceCents4dp: bigint | null
}

export interface PayoutPreviewProps {
  quote: Quote
  outcome: Outcome
  /** Blanks every value: nothing is reachable, so nothing is quotable. */
  empty?: boolean
  /** Present only for a sell. See the header. */
  sell?: SellPreview | null
  className?: string
}

export const PayoutPreview = memo(function PayoutPreview({
  quote,
  outcome,
  empty,
  sell,
  className,
}: PayoutPreviewProps) {
  const blank = empty || quote.filledShares <= 0n
  const winTone = outcome === 'UP' ? 'text-up-text' : 'text-down-text'
  // Nothing is being minted, so there is no cost, no payout and no return —
  // and a close's own average is the sell price, not the complement's.
  const mintless = sell !== null && sell !== undefined && sell.openingShares <= 0n
  const avgCents4dp = sell?.sellPriceCents4dp ?? quote.avgPriceCents4dp

  return (
    <div className={cx('flex flex-col gap-0_5', className)} data-testid="payout-preview">
      {sell && sell.coveredShares > 0n ? (
        <Row label="Proceeds" value={blank ? '—' : usd(sell.proceedsMicroUsdc)} />
      ) : null}
      {mintless ? null : <Row label="Cost" value={blank ? '—' : usd(quote.costMicroUsdc)} />}
      <Row label="Fee" value={feeText(quote)} />
      {mintless ? null : (
        <Row label="To win" value={blank ? '—' : usd(quote.payoutMicroUsdc)} valueClassName={winTone} />
      )}
      {mintless ? null : <Row label="Return" value={blank ? '—' : signedPct(quote.returnTenths)} />}
      <Row
        label={quote.levels.length > 1 ? 'Est. avg' : 'Avg price'}
        value={blank || avgCents4dp === null ? '—' : `${formatFixed(avgCents4dp, 4)}¢`}
        // The slippage pill sits beside the average, in `warning-text`, because
        // walking the book is the thing the average is hiding.
        badge={quote.slipTenths > 0n ? `+${formatFixed(quote.slipTenths, 1)}% slip` : null}
      />
    </div>
  )
})

// ---------------------------------------------------------------------------

/**
 * `Fee $0.00`, or a range if one ever appears.
 *
 * Both endpoints are rendered when they differ, because a single number the
 * receipt can contradict is exactly what §7.9 forbids: the fill price is not
 * known until 250 ms after the click, so a point estimate is a promise the venue
 * has not made.
 */
function feeText(quote: Quote): string {
  if (quote.feeMinMicroUsdc === quote.feeMaxMicroUsdc) return usd(quote.feeMinMicroUsdc)
  return `${usd(quote.feeMinMicroUsdc)} – ${usd(quote.feeMaxMicroUsdc)}`
}

function usd(micro: bigint): string {
  return formatUsdc(formatFixed(micro, USDC_DP))
}

/** Always signed, per §2.4's "Return +58.7% ← always signed". */
function signedPct(tenths: bigint | null): string {
  if (tenths === null) return '—'
  const sign = tenths < 0n ? '-' : '+'
  const abs = tenths < 0n ? -tenths : tenths
  return `${sign}${formatFixed(abs, 1)}%`
}

function Row({
  label,
  value,
  valueClassName,
  badge,
}: {
  label: string
  value: string
  valueClassName?: string
  badge?: string | null
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-body-sm">
      <span className="text-text-secondary">{label}</span>
      <span className="flex items-baseline gap-1_5">
        {badge ? <span className="text-caption text-warning-text">{badge}</span> : null}
        <span className={cx('ud-num text-text-primary', valueClassName)}>{value}</span>
      </span>
    </div>
  )
}
